import React, { useEffect, useState } from "react";
import { useAuthStore } from "../../stores/authStore";
import { useNavigate } from "react-router-dom";
import useGenerateDeviceToken from "../../hooks/useGenerateDeviceToken";
import { Eye, EyeOff } from "lucide-react";
import { colors } from "../../colors/colors";
import { AxiosError } from "axios";
import { sendLoginCode } from "../../axios/forgotPassword";
import { getAuthErrorMessage } from "../../utils/authErrors";

import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
} from "@mui/material";

import {
  loginContainerSx,
  paperSx,
  titleSx,
  formSx,
  labelSx,
  passwordBoxSx,
  buttonSubmitSx,
  textFieldSx,
  secondaryButtonSx,
  errorMessageSx,
  infoMessageSx,
} from "./styles";
import { HomeIcon } from "../../assets/icon";
import { PasswordRequirements } from "./password-requirements";
import { isPasswordValid } from "./passwordValidation";

const LoginScreen = () => {
  const navigate = useNavigate();
  const [login, setLogin] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [code, setCode] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [infoMessage, setInfoMessage] = useState<string>("");
  const [isCodeLoginMode, setIsCodeLoginMode] = useState<boolean>(false);

  const [showResetPasswordForm, setShowResetPasswordForm] =
    useState<boolean>(false);
  const [passwordExpiryReason, setPasswordExpiryReason] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [resetErrorMessage, setResetErrorMessage] = useState<string>("");
  const [showPasswordRequirementsError, setShowPasswordRequirementsError] =
    useState<boolean>(false);

  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);

  const { requestAndSendToken } = useGenerateDeviceToken();
  const {
    login: loginUser,
    loginWithCode,
    authError,
    updatePassword,
  } = useAuthStore();

  useEffect(() => {
    if (sessionStorage.getItem("controlsManualToggled")) {
      sessionStorage.removeItem("controlsManualToggled");
    }
  }, []);

  useEffect(() => {
    if (authError) {
      setErrorMessage(authError);
    }
  }, [authError]);

  const validatePassword = isPasswordValid;

  const navigateWithChallengeData = () => {
    const savedParams = sessionStorage.getItem("pendingChallengeData");

    if (savedParams) {
      try {
        const params = JSON.parse(savedParams);
        const searchParams = new URLSearchParams();

        if (params.domain) searchParams.set("domain", params.domain);
        if (params.focus) searchParams.set("focus", params.focus);
        if (params.message_id)
          searchParams.set("message_id", params.message_id);
        if (params.roleplay) searchParams.set("roleplay", params.roleplay);

        sessionStorage.removeItem("pendingChallengeData");

        const queryString = searchParams.toString();
        const targetUrl = `/chat${queryString ? `?${queryString}` : ""}`;

        window.location.href = targetUrl;
      } catch (error) {
        console.error("Error parsing saved challenge data:", error);
        sessionStorage.removeItem("pendingChallengeData");
        navigate("/chat");
      }
    } else {
      navigate("/chat");
    }
  };

  const handleRequestOneTimeCode = async () => {
    if (!login.trim()) {
      setErrorMessage("Please enter your login or email first.");
      return;
    }

    setLoading(true);
    setErrorMessage("");
    setInfoMessage("");

    try {
      const response = await sendLoginCode(login.trim());
      if (response) {
        setIsCodeLoginMode(true);
        setPassword("");
        setCode("");
        setInfoMessage("Code has been sent to your email.");
      } else {
        throw new Error("Unknown sending code error");
      }
    } catch (error) {
      let message = "Unknown sending code error";
      if (error && typeof error === "object") {
        if (error instanceof AxiosError) {
          if (error.status === 400) {
            message = "Wrong login";
          } else if (error.status === 429) {
            const authMessage = getAuthErrorMessage(error);
            if (authMessage) message = authMessage;
          }
        } else if ("message" in error && typeof error.message === "string") {
          message = error.message;
        }
      } else if (typeof error === "string") {
        message = error;
      }
      setErrorMessage(message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!login.trim()) {
      setErrorMessage("Please enter your login.");
      return;
    }

    if (isCodeLoginMode) {
      if (!code.trim()) {
        setErrorMessage("Please enter code from email.");
        return;
      }
      if (!Number.isInteger(Number(code.trim()))) {
        setErrorMessage("Code must contain only numbers.");
        return;
      }
    } else if (!password.trim()) {
      setErrorMessage("Please enter both login and password.");
      return;
    }

    setLoading(true);
    setErrorMessage("");
    setInfoMessage("");

    try {
      if (isCodeLoginMode) {
        await loginWithCode(login.trim(), Number(code.trim()));
        await requestAndSendToken();
        await new Promise((resolve) => setTimeout(resolve, 100));
        navigateWithChallengeData();
      } else {
        await loginUser(login, password);
        await requestAndSendToken();
        await new Promise((resolve) => setTimeout(resolve, 100));
        navigateWithChallengeData();
      }
    } catch (error: any) {
      if (error instanceof AxiosError) {
        if (error.status === 429) {
          const authMessage = getAuthErrorMessage(error);
          if (authMessage) setErrorMessage(authMessage);
          return;
        }

        if (isCodeLoginMode && error.status === 401) {
          setErrorMessage("Wrong code");
          return;
        }

        if (isCodeLoginMode && error.status === 400) {
          setErrorMessage("Wrong login");
          return;
        }
      }

      if (
        error.message === "Password expired" ||
        error.message === "The password is temporary."
      ) {
        setShowResetPasswordForm(true);
        setPasswordExpiryReason(
          error.message === "Password expired"
            ? "Password expired"
            : "The password is temporary.",
        );
      } else {
        setErrorMessage(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetErrorMessage("");
    setShowPasswordRequirementsError(false);

    if (!validatePassword(newPassword)) {
      setShowPasswordRequirementsError(true);
      return;
    }
    if (newPassword !== confirmPassword) {
      setResetErrorMessage("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      await updatePassword(newPassword);
      await requestAndSendToken();
      await new Promise((resolve) => setTimeout(resolve, 100));
      navigateWithChallengeData();
    } catch (error: any) {
      setResetErrorMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={loginContainerSx}>
      <Paper elevation={6} sx={paperSx}>
        <Typography sx={titleSx}>Welcome Back!</Typography>
        {showResetPasswordForm ? (
          <Box component="form" onSubmit={handleResetPassword} sx={formSx}>
            {passwordExpiryReason && (
              <Typography variant="body2" sx={infoMessageSx}>
                {passwordExpiryReason}
              </Typography>
            )}
            <PasswordRequirements
              password={newPassword}
              showValidation={showPasswordRequirementsError}
            />
            {resetErrorMessage && (
              <Typography variant="body2" sx={errorMessageSx}>
                {resetErrorMessage}
              </Typography>
            )}
            <Box>
              <Typography
                variant="body2"
                fontWeight="medium"
                component="label"
                htmlFor="new-password"
                sx={labelSx(true)}
              >
                New Password
              </Typography>
              <TextField
                sx={textFieldSx(true)}
                id="new-password"
                variant="outlined"
                fullWidth
                required
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  setShowPasswordRequirementsError(false);
                }}
                placeholder="Enter new password"
                InputProps={{
                  sx: {
                    "& input::-ms-reveal": {
                      display: "none",
                    },
                    "& input::-ms-clear": {
                      display: "none",
                    },
                  },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword((prev) => !prev)}
                        edge="end"
                        sx={{ color: colors.secondary }}
                      >
                        {showNewPassword ? (
                          <EyeOff size={20} />
                        ) : (
                          <Eye size={20} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                  type: showNewPassword ? "text" : "password",
                }}
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              <Typography
                variant="body2"
                fontWeight="medium"
                component="label"
                htmlFor="confirm-password"
                sx={labelSx(true)}
              >
                Confirm Password
              </Typography>
              <TextField
                sx={textFieldSx(true)}
                id="confirm-password"
                variant="outlined"
                fullWidth
                required
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter new password"
                InputProps={{
                  sx: {
                    "& input::-ms-reveal": {
                      display: "none",
                    },
                    "& input::-ms-clear": {
                      display: "none",
                    },
                  },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        edge="end"
                        sx={{ color: colors.secondary }}
                      >
                        {showConfirmPassword ? (
                          <EyeOff size={20} />
                        ) : (
                          <Eye size={20} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Button
              type="submit"
              sx={buttonSubmitSx(loading)}
              disabled={loading}
            >
              {loading ? "Resetting..." : "Reset Password"}
            </Button>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleLogin} sx={formSx}>
            {infoMessage && (
              <Typography variant="body2" sx={infoMessageSx}>
                {infoMessage}
              </Typography>
            )}
            {errorMessage && (
              <Typography variant="body2" sx={errorMessageSx}>
                {errorMessage}
              </Typography>
            )}
            <Box>
              <Typography
                variant="body2"
                fontWeight="medium"
                component="label"
                htmlFor="login"
                sx={labelSx(true)}
              >
                Login
              </Typography>
              <TextField
                sx={textFieldSx(true)}
                id="login"
                variant="outlined"
                fullWidth
                required
                value={login}
                onChange={(e) => {
                  setLogin(e.target.value);
                  setErrorMessage("");
                }}
                placeholder="Enter your login or email"
              />
            </Box>
            <Box sx={passwordBoxSx}>
              <Typography
                variant="body2"
                fontWeight="medium"
                component="label"
                htmlFor={isCodeLoginMode ? "code" : "password"}
                sx={labelSx(true)}
              >
                {isCodeLoginMode ? "Code" : "Password"}
              </Typography>
              <TextField
                sx={textFieldSx(true)}
                id={isCodeLoginMode ? "code" : "password"}
                variant="outlined"
                fullWidth
                required
                type={
                  isCodeLoginMode ? "text" : showPassword ? "text" : "password"
                }
                value={isCodeLoginMode ? code : password}
                onChange={(e) => {
                  if (isCodeLoginMode) {
                    setCode(e.target.value);
                  } else {
                    setPassword(e.target.value);
                  }
                  setErrorMessage("");
                }}
                placeholder={
                  isCodeLoginMode
                    ? "Enter code from your email"
                    : "Enter your password"
                }
                InputProps={
                  isCodeLoginMode
                    ? undefined
                    : {
                        sx: {
                          "& input::-ms-reveal": {
                            display: "none",
                          },
                          "& input::-ms-clear": {
                            display: "none",
                          },
                        },
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword((prev) => !prev)}
                              edge="end"
                              sx={{ color: colors.secondary }}
                            >
                              {showPassword ? (
                                <EyeOff size={20} />
                              ) : (
                                <Eye size={20} />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                        type: showPassword ? "text" : "password",
                      }
                }
              />
            </Box>
            <Button
              type="submit"
              sx={buttonSubmitSx(loading)}
              disabled={loading}
              startIcon={!loading ? <HomeIcon /> : undefined}
            >
              {loading ? "Logging in..." : "Login"}
            </Button>

            <Button
              type="button"
              variant="text"
              sx={secondaryButtonSx(loading)}
              disabled={loading}
              onClick={() => {
                void handleRequestOneTimeCode();
              }}
            >
              {isCodeLoginMode ? "Resend code" : "Use one time code"}
            </Button>

            {isCodeLoginMode && (
              <Button
                type="button"
                variant="text"
                sx={secondaryButtonSx(loading)}
                disabled={loading}
                onClick={() => {
                  setIsCodeLoginMode(false);
                  setCode("");
                  setInfoMessage("");
                  setErrorMessage("");
                }}
              >
                Use password
              </Button>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default LoginScreen;
